import React, { useState } from 'react';
import { Highlight } from 'prism-react-renderer';
import { Check, Copy } from './Icons';

// Brand code theme: deep Veritage purple with lavender/mint accents.
const theme = {
  plain: { color: '#E9E5FF', backgroundColor: 'transparent' },
  styles: [
    { types: ['comment', 'prolog', 'doctype', 'cdata'], style: { color: '#8B84A8', fontStyle: 'italic' } },
    { types: ['punctuation', 'operator'],               style: { color: '#A69FC4' } },
    { types: ['property', 'tag', 'attr-name'],           style: { color: '#C9B8FF' } },
    { types: ['string', 'attr-value', 'char'],           style: { color: '#9FE8C7' } },
    { types: ['number', 'boolean', 'constant'],          style: { color: '#FFD08A' } },
    { types: ['keyword', 'selector', 'important'],       style: { color: '#F5A8FF' } },
    { types: ['function', 'class-name'],                 style: { color: '#8FD3FF' } },
    { types: ['variable', 'regex'],                      style: { color: '#FFB8C6' } },
  ],
};

const LANG_ALIASES = { js: 'javascript', ts: 'typescript', sh: 'bash', shell: 'bash', html: 'markup', xml: 'markup' };
const LANG_LABELS = { http: 'HTTP', json: 'JSON', javascript: 'JavaScript', typescript: 'TypeScript', markup: 'HTML', bash: 'Shell', jsx: 'JSX', tsx: 'TSX', python: 'Python', java: 'Java' };

function HighlightedCode({ code, language }) {
  return (
    <Highlight code={code} language={language} theme={theme}>
      {({ tokens, getLineProps, getTokenProps }) =>
        tokens.map((line, i) => (
          <div key={i} {...getLineProps({ line })}>
            {line.map((token, key) => <span key={key} {...getTokenProps({ token })} />)}
          </div>
        ))
      }
    </Highlight>
  );
}

/** Raw HTTP: request line + headers styled by hand, body highlighted as JSON. */
function HttpCode({ code }) {
  const lines = code.split('\n');
  const blank = lines.findIndex((l) => l.trim() === '');
  const head = blank === -1 ? lines : lines.slice(0, blank);
  const body = blank === -1 ? '' : lines.slice(blank + 1).join('\n');
  return (
    <>
      {head.map((line, i) => {
        if (i === 0) {
          const [method, ...rest] = line.split(' ');
          return (
            <div key={i}>
              <span className="tok-http-method">{method}</span>{' '}
              <span className="tok-http-url">{rest.join(' ')}</span>
            </div>
          );
        }
        const idx = line.indexOf(':');
        return idx > 0 ? (
          <div key={i}>
            <span className="tok-http-header">{line.slice(0, idx)}</span>
            <span className="tok-http-punct">:</span>
            <span className="tok-http-value">{line.slice(idx + 1)}</span>
          </div>
        ) : <div key={i}>{line}</div>;
      })}
      {blank !== -1 && <div>&nbsp;</div>}
      {body && (body.trim().startsWith('{') || body.trim().startsWith('['))
        ? <HighlightedCode code={body} language="json" />
        : body && <div>{body}</div>}
    </>
  );
}

export default function CodeBlock({ code, lang = '', title }) {
  const [copied, setCopied] = useState(false);
  const language = LANG_ALIASES[lang] || lang || 'text';
  const label = title || LANG_LABELS[language] || (lang ? lang.toUpperCase() : 'Code');

  const handleCopy = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="docs-code">
      <div className="docs-code-header">
        <span className="docs-code-dots" aria-hidden="true"><i /><i /><i /></span>
        <span className="docs-code-label">{label}</span>
        <button type="button" className={`docs-code-copy${copied ? ' is-copied' : ''}`} onClick={handleCopy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="docs-code-body"><code>
        {language === 'http'
          ? <HttpCode code={code} />
          : language === 'text'
            ? code
            : <HighlightedCode code={code} language={language} />}
      </code></pre>
    </div>
  );
}
